import Link from 'next/link';
import FaIcon from '../../../../components/icon/FaIcon/FaIcon';
import { faClock, faTruck } from '../../../../components/icon/FaIcon/FaIcon';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import ScrollFade from '../../../../components/ui/ScrollFade/ScrollFade';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import type { VariantType } from '../../../../types';
import type { LotteryEvent } from '../../types';
import fanychanceStyles from './LotteryEventList.fanychance.module.scss';
import junonkujiStyles from './LotteryEventList.junonkuji.module.scss';
import styles from './LotteryEventList.module.scss';
import raffleStyles from './LotteryEventList.raffle.module.scss';

export type LotteryEventListProps = {
  lotteryEventList: Array<LotteryEvent>;
  variant?: VariantType;
  isSubImage?: boolean;
  isLabelButton?: boolean;
  // 外部依存をpropsとして受け取る
  LOTTERY_STATUS?: {
    upcoming: { text: string; value: string };
    is_session: { text: string; value: string };
    later: { text: string; value: string };
  };
  estimatedShippingDateText?: (params: { eventEndAt: string; estimatedShippingDate?: string }) => string;
  eventStatusLabel?: (status: string) => string;
  showEstimatedShippingDate?: (endAt: string, estimatedShippingDate: string | null) => boolean;
  Format?: { dateTimeLotteryJa: string };
  formatDateCommon?: (date: string, format: string) => string;
};

function LotteryEventList({
  lotteryEventList,
  variant = 'default',
  isSubImage = false,
  isLabelButton = true,
  LOTTERY_STATUS,
  estimatedShippingDateText,
  eventStatusLabel,
  showEstimatedShippingDate,
  Format,
  formatDateCommon,
}: LotteryEventListProps) {
  // variantに応じてスタイルを決定
  // var(--color-primary)があるので、出し分ける。
  const getVariantStyles = () => {
    switch (variant) {
      case 'raffle':
        return raffleStyles;
      case 'fanychance':
        return fanychanceStyles;
      case 'junonkuji':
        return junonkujiStyles;
      default:
        return styles;
    }
  };

  const currentStyles = getVariantStyles();

  // イベントステータスラベルボタン（isLabelButtonがtrueの場合）
  const eventStatusLabelButton = (eventStatus: string) => {
    if (!LOTTERY_STATUS) return null;

    switch (eventStatus) {
      case LOTTERY_STATUS.upcoming.value:
        return (
          <div className={`${currentStyles.labelButton} ${currentStyles.bgLightBlue}`}>
            {LOTTERY_STATUS.upcoming.text}
          </div>
        );
      case LOTTERY_STATUS.is_session.value:
        return (
          <div className={`${currentStyles.labelButton} ${currentStyles.bgPrimary}`}>
            {LOTTERY_STATUS.is_session.text}
          </div>
        );
      case LOTTERY_STATUS.later.value:
        return (
          <div className={`${currentStyles.labelButton} ${currentStyles.bgLightGray}`}>{LOTTERY_STATUS.later.text}</div>
        );
      default:
        return (
          <div className={`${currentStyles.labelButton} ${currentStyles.bgPrimary}`}>
            {LOTTERY_STATUS.is_session.text}
          </div>
        );
    }
  };

  // ダミー要素の生成
  const dummy = () => {
    const result = [];
    for (let i = 0; i < 4; i++) {
      result.push(
        <li className={currentStyles.dummy} key={`dummy_${i}`}>
          <div className={currentStyles.dummyBlock} />
        </li>,
      );
    }
    return result;
  };

  return (
    <ul className={currentStyles.componentStyle}>
      {lotteryEventList.map((event) => (
        <ScrollFade key={event.id} tag='li'>
          <Link href={`/lotteries/${event.id}`} className={currentStyles.itemBlock} prefetch={false}>
            <div className={currentStyles.imageWrapper}>
              <ImageView src={isSubImage ? event.subImageUrl : event.imageUrl} isFreeSize={!isSubImage} />
            </div>
            {isLabelButton ? (
              <div className={currentStyles.labelButtonWrapper}>{eventStatusLabelButton(event.status)}</div>
            ) : (
              <div className={currentStyles.itemStatus}>
                <img src={eventStatusLabel?.(event.status)} alt='' />
              </div>
            )}
            <div className={`${currentStyles.itemName} ${utilsStyles.textBold}`}>{event.name}</div>
            {event.endAt && (
              <div className={currentStyles.itemDate}>
                <FaIcon icon={faClock} />
                &nbsp;{formatDateCommon?.(event.endAt, Format?.dateTimeLotteryJa || '')}
                &nbsp;まで
              </div>
            )}
            {showEstimatedShippingDate?.(event.endAt, event.estimatedShippingDate) && (
              <div className={currentStyles.estimatedDeliveryDate}>
                <FaIcon icon={faTruck} />
                &nbsp;
                {estimatedShippingDateText?.({
                  eventEndAt: event.endAt,
                  estimatedShippingDate: event.estimatedShippingDate || undefined,
                })}
              </div>
            )}
          </Link>
        </ScrollFade>
      ))}
      {/* ダミー */}
      {dummy()}
    </ul>
  );
}

export default LotteryEventList;
